// Setup coaching in Counter Intelligence — turns the setup-progress edge
// payload (the same data behind the dashboard's "Setup progress" card and the
// sidebar checklist) into an in-chat coach card: progress pills, the remaining
// steps with their one-line explanations and deep links, and action chips that
// route into the assistant's own flows ("Update prices from an invoice",
// "Create an order template", …). Pure, so the card shape is unit-testable.

// What Counter Intelligence can actively help with, per setup step. Order
// follows the steps; chips are deduped and capped so the card stays compact.
const STEP_CHIPS = {
  low_item_count: 'Import my items',
  missing_prices: 'Update prices from an invoice',
  missing_vendor_contacts: 'Help me add vendor contacts',
  missing_order_templates: 'Create an order template',
  missing_eop_template: 'Create an End of Period template',
  low_par_levels: 'Help me set PAR levels',
  unmapped_pnl_categories: 'How do I map P&L categories?',
};

const MAX_STEPS = 5;
const MAX_CHIPS = 4;
const MAX_DESCRIPTION_CHARS = 170;

// Prefer the step's descriptive `message` (the same copy the dashboard card
// shows) when it's chat-sized; the template steps carry multi-sentence
// messages, so those fall back to the one-line `reason`.
const descriptionFor = (step) => {
  const message = typeof step.message === 'string' ? step.message.trim() : '';
  if (message && message.length <= MAX_DESCRIPTION_CHARS) return message;
  const reason = typeof step.reason === 'string' ? step.reason.trim() : '';
  return reason || message.slice(0, MAX_DESCRIPTION_CHARS);
};

/**
 * Build the chat coach card from a setup-progress payload.
 * Returns null when setup is complete or the payload is unusable — the chat
 * stops coaching the moment there is nothing left to coach.
 */
export function buildSetupCoachCard(setupPayload) {
  const summary = setupPayload?.summary;
  if (!summary || !Number(summary.total_count)) return null;
  const remaining = Number(summary.remaining_count || 0);
  if (remaining <= 0) return null;

  const steps = (Array.isArray(setupPayload.steps) ? setupPayload.steps : [])
    .filter((s) => s && s.key && s.title)
    .slice(0, MAX_STEPS)
    .map((s) => ({
      key: s.key,
      title: s.title,
      description: descriptionFor(s),
      href: s.href || null,
      cta: s.cta || 'Open',
    }));
  if (steps.length === 0) return null;

  const chips = [];
  const seen = new Set();
  steps.forEach((s) => {
    const label = STEP_CHIPS[s.key];
    if (label && !seen.has(label) && chips.length < MAX_CHIPS) {
      seen.add(label);
      chips.push({ label });
    }
  });

  return {
    kind: 'setup_coach',
    completed: Number(summary.completed_count || 0),
    total: Number(summary.total_count || 0),
    remaining,
    steps,
    chips,
  };
}

/** Session key gating the auto-coach to once per sign-in/tab per store. */
export function setupCoachSessionKey(storeId) {
  return `acc_setup_coach_shown_${storeId || 'store'}`;
}
